import { cn } from "../../../../lib/utils";
import RecipeVariantColumnHeader from "./RecipeVariantColumnHeader";
import { useVariantNameInlineEdit } from "./hooks/useVariantNameInlineEdit";
import { RecipeVariantColumn } from "./recipeVariantTable.types";

type Props = {
  fixedColumns: RecipeVariantColumn[];
  dynamicColumns: RecipeVariantColumn[];
  variants: Array<{ _id?: string; variantName?: string }>;
  recipeId: string;
  addDeleteColumn?: boolean;
  readOnly?: boolean;
  onVariantNameUpdated?: () => void;
  onOpenEditVariation?: (variantId: string) => void;
};

const RecipeVariantTableHeader = ({
  fixedColumns,
  dynamicColumns,
  variants,
  recipeId,
  addDeleteColumn = false,
  readOnly = false,
  onVariantNameUpdated,
  onOpenEditVariation
}: Props) => {
  const {
    editingVariantId,
    editNameValue,
    setEditNameValue,
    startEdit,
    cancelEdit,
    saveEdit,
    isSaving,
    variantNameMaxLength
  } = useVariantNameInlineEdit({
    recipeId,
    variants,
    readOnly,
    onSuccess: onVariantNameUpdated
  });

  const renderFixedHeaderCell = (col: RecipeVariantColumn) => {
    return (
      <th
        key={`header-${col.id ?? col.title}`}
        className={cn(
          "tw-px-4 tw-py-3 tw-font-bold tw-whitespace-nowrap tw-bg-primary/5 tw-text-primary",
          col.custom ? "tw-text-left tw-min-w-[180px]" : "tw-text-center tw-min-w-[140px]"
        )}
      >
        <div
          className={cn(
            "tw-flex tw-w-full tw-items-center",
            col.custom ? "tw-justify-start" : "tw-justify-center"
          )}
        >
          <span className="tw-line-clamp-2 tw-font-bold tw-text-primary">{col.title}</span>
        </div>
      </th>
    );
  };

  return (
    <thead>
      <tr className="tw-border-b tw-border-border">
        {fixedColumns.map(renderFixedHeaderCell)}
        {dynamicColumns.map(col => (
          <RecipeVariantColumnHeader
            key={`header-${col.id ?? col.title}`}
            column={col}
            readOnly={readOnly}
            isEditing={editingVariantId === col.variantId}
            editNameValue={editNameValue}
            isSaving={isSaving}
            variantNameMaxLength={variantNameMaxLength}
            onEditNameValueChange={setEditNameValue}
            onStartEdit={() => startEdit(col)}
            onSaveEdit={() => col.variantId && saveEdit(col.variantId)}
            onCancelEdit={cancelEdit}
            onOpenEditVariation={() => col.variantId && onOpenEditVariation?.(col.variantId)}
          />
        ))}
        {addDeleteColumn && dynamicColumns.length > 0 && (
          <th className="tw-w-10 tw-bg-primary/5" aria-label="Actions" />
        )}
      </tr>
    </thead>
  );
};

export default RecipeVariantTableHeader;
